/**
 * Country Code Metafield Sync Module
 * 
 * This module handles syncing the country_code to customer meta fields:
 * - Syncs on page load if customer is logged in
 * - Updates when country selector changes
 * - Exposes country_code globally via window.countryCode
 */

class CountryMetafieldSync {
  constructor() {
    this.countryCode = null;
    this.customerId = null;
    this.metafieldNamespace = 'custom';
    this.metafieldKey = 'country_code';
    this.isSyncing = false;
    
    // Get initial country code from the page
    this.init();
  }

  init() {
    // Get country code from exposed window variable or localization data
    if (window.currentCountryCode) {
      this.countryCode = window.currentCountryCode;
    } else {
      const countryInput = document.querySelector('input[name="country_code"]');
      if (countryInput) {
        this.countryCode = countryInput.value;
      }
    }

    // Get customer ID if logged in (exposed from theme.liquid)
    if (window.Shopify && window.Shopify.customerId) {
      this.customerId = window.Shopify.customerId;
    }

    // Check localStorage for previously stored country code if not set from page
    if (!this.countryCode && typeof Storage !== 'undefined') {
      const storedCode = localStorage.getItem('shopify_country_code');
      if (storedCode) {
        this.countryCode = storedCode;
      }
    }

    // Expose country code globally with reactive getter/setter
    try {
      // Delete existing property if it exists to allow redefinition
      if (window.countryCode !== undefined) {
        delete window.countryCode;
      }
      
      Object.defineProperty(window, 'countryCode', {
        get: () => this.countryCode,
        set: (value) => {
          if (value && value !== this.countryCode) {
            this.countryCode = value;
            this.syncToMetafield(value);
          }
        },
        enumerable: true,
        configurable: true
      });
      
      // Set initial value
      window.countryCode = this.countryCode;
    } catch (e) {
      // Fallback if defineProperty fails
      window.countryCode = this.countryCode;
    }

    // Sync on page load if customer is logged in
    if (this.shouldSync()) {
      this.syncToMetafield(this.countryCode);
    }

    // Always sync localStorage with current country code on page load
    // This ensures localStorage matches the actual current country, not an old value
    if (this.countryCode) {
      this.updateLocalStorageSync(this.countryCode);
    }

    // Listen for country selector changes
    this.setupCountrySelectorListener();

    // Listen for country code change events that may have fired before this module loaded
    window.addEventListener('countryCodeChanged', (event) => {
      if (event.detail && event.detail.countryCode) {
        this.updateCountryCode(event.detail.countryCode);
      }
    });

    // Watch for customer login (if customer ID appears later)
    this.setupCustomerLoginWatcher();
  }

  /**
   * Watch for customer login to sync stored country code
   */
  setupCustomerLoginWatcher() {
    // Check periodically if customer logs in after page load
    const checkInterval = setInterval(() => {
      if (!this.customerId && window.Shopify && window.Shopify.customerId) {
        this.customerId = window.Shopify.customerId;
        
        // Sync stored country code now that customer is logged in
        if (this.countryCode && this.shouldSync()) {
          this.syncToMetafield(this.countryCode);
        }
        
        // Stop checking once we have a customer ID
        clearInterval(checkInterval);
      }
    }, 2000); // Check every 2 seconds

    // Stop checking after 30 seconds to avoid infinite checking
    setTimeout(() => clearInterval(checkInterval), 30000);
  }

  shouldSync() {
    // Only sync if we have a customer ID and a country code
    if (!this.customerId || !this.countryCode) {
      return false;
    }
    
    // Check if we're on a customer account page where the API is available
    // The Customer Account API is typically only available on account pages
    const isAccountPage = window.location.pathname.startsWith('/account/') || 
                         window.location.pathname.includes('/account');
    
    // If not on account page, skip API sync and use localStorage instead
    if (!isAccountPage) {
      return false;
    }
    
    return true;
  }

  /**
   * Sync country_code to customer meta field
   */
  async syncToMetafield(countryCode) {
    if (!countryCode || this.isSyncing) {
      return;
    }

    // If not logged in or not on account page, use alternative sync method
    if (!this.shouldSync()) {
      await this.tryAlternativeSync(countryCode);
      return;
    }

    this.isSyncing = true;

    try {
      // Use Shopify Customer Account API
      // Only attempt if we're on an account page where the API is available
      const apiVersions = ['2024-01', '2023-10', '2023-07'];
      let success = false;

      // Try POST requests (we've already checked we're on an account page in shouldSync)
      for (const version of apiVersions) {
        try {
          const response = await fetch(`/account/customer/api/${version}/metafields`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              metafield: {
                namespace: this.metafieldNamespace,
                key: this.metafieldKey,
                type: 'single_line_text_field',
                value: countryCode
              }
            }),
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            this.countryCode = countryCode;
            success = true;
            break;
          } else if (response.status === 404) {
            // API endpoint not available for this version, try next version silently
            continue;
          }
          // For other errors, continue to next version
        } catch (err) {
          // Network error or other exception, continue to next version silently
          continue;
        }
      }

      if (!success) {
        // Try alternative sync method if API calls failed
        await this.tryAlternativeSync(countryCode);
      }
    } catch (error) {
      // Silently fall back to localStorage if API sync fails
      await this.tryAlternativeSync(countryCode);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Alternative sync method
   * This works when the Customer Account API isn't available or user is not logged in
   */
  async tryAlternativeSync(countryCode) {
    try {
      // Store in localStorage as a fallback so it persists across sessions
      // This is synchronous so it happens before page reload
      if (typeof Storage !== 'undefined') {
        localStorage.setItem('shopify_country_code', countryCode);
      }

      // If customer is logged in but API failed, we can attempt a different approach
      // This might require a Shopify app or custom endpoint
      // For now, we'll at least store it locally and dispatch events

      // Dispatch custom event for other scripts to listen to
      window.dispatchEvent(new CustomEvent('countryCodeUpdated', {
        detail: { countryCode: countryCode }
      }));

      // Update the global variable
      if (window.countryCode !== undefined) {
        window.countryCode = countryCode;
      }
    } catch (error) {
      // Silently handle alternative sync failures
    }
  }

  /**
   * Immediately update localStorage (synchronous, for use before page reload)
   */
  updateLocalStorageSync(countryCode) {
    try {
      if (typeof Storage !== 'undefined' && countryCode) {
        localStorage.setItem('shopify_country_code', countryCode);
      }
    } catch (error) {
      // Silently handle localStorage errors
    }
  }

  /**
   * Setup listener for country selector changes
   */
  setupCountrySelectorListener() {
    // Listen for clicks on country selector links
    document.addEventListener('click', (event) => {
      const countryLink = event.target.closest('a[data-value]');
      if (countryLink && countryLink.dataset.value) {
        const newCountryCode = countryLink.dataset.value;
        
        // Immediately update localStorage (synchronous, before form submits/page reloads)
        this.updateLocalStorageSync(newCountryCode);
        
        // Update country code immediately
        this.countryCode = newCountryCode;
        if (window.countryCode !== undefined) {
          window.countryCode = newCountryCode;
        }

        // Sync to meta field
        if (this.shouldSync()) {
          this.syncToMetafield(newCountryCode);
        }

        // Dispatch event
        window.dispatchEvent(new CustomEvent('countryCodeUpdated', {
          detail: { countryCode: newCountryCode }
        }));
      }
    });

    // Also listen for form submissions on localization forms
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form.closest('localization-form')) {
        const countryInput = form.querySelector('input[name="country_code"]');
        if (countryInput && countryInput.value) {
          const newCountryCode = countryInput.value;
          
          // Ensure localStorage is updated before form submits
          this.updateLocalStorageSync(newCountryCode);
          
          this.countryCode = newCountryCode;
          
          if (this.shouldSync()) {
            this.syncToMetafield(newCountryCode);
          }
        }
      }
    });
  }

  /**
   * Get current country code
   */
  getCountryCode() {
    return this.countryCode;
  }

  /**
   * Update country code manually (can be called from other scripts)
   */
  updateCountryCode(countryCode) {
    if (!countryCode) return;
    
    this.countryCode = countryCode;
    
    // Update hidden input if it exists
    const countryInput = document.querySelector('input[name="country_code"]');
    if (countryInput) {
      countryInput.value = countryCode;
    }

    // Immediately update localStorage (synchronous, before page reload)
    this.updateLocalStorageSync(countryCode);

    // Sync to meta field (async, will happen if customer is logged in)
    if (this.shouldSync()) {
      this.syncToMetafield(countryCode);
    } else {
      // Still dispatch event even if not logged in
      this.tryAlternativeSync(countryCode);
    }

    // Dispatch event
    window.dispatchEvent(new CustomEvent('countryCodeUpdated', {
      detail: { countryCode: countryCode }
    }));
  }

  /**
   * Force sync country code to metadata (easiest way to sync)
   * Can be called from anywhere, attempts sync even if not on account page
   * Usage: window.countryMetafieldSync.forceSync()
   *        window.countryMetafieldSync.forceSync('US') // with specific country code
   */
  async forceSync(countryCode = null) {
    const codeToSync = countryCode || this.countryCode || window.countryCode;
    
    if (!codeToSync) {
      console.warn('[Country Sync] No country code available to sync');
      return false;
    }

    // Update local state
    this.countryCode = codeToSync;
    if (window.countryCode !== undefined) {
      window.countryCode = codeToSync;
    }

    // Update localStorage
    this.updateLocalStorageSync(codeToSync);

    // Try to sync to metadata if user is logged in
    // First check if we're on account page (normal sync)
    if (this.shouldSync()) {
      await this.syncToMetafield(codeToSync);
      return true;
    }

    // If not on account page but user is logged in, try Customer Account API anyway
    // Some Shopify stores allow this from any page
    if (this.customerId && codeToSync) {
      this.isSyncing = true;
      
      try {
        const apiVersions = ['2024-01', '2023-10', '2023-07'];
        
        for (const version of apiVersions) {
          try {
            const response = await fetch(`/account/customer/api/${version}/metafields`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                metafield: {
                  namespace: this.metafieldNamespace,
                  key: this.metafieldKey,
                  type: 'single_line_text_field',
                  value: codeToSync
                }
              }),
              credentials: 'include'
            });

            if (response.ok) {
              this.countryCode = codeToSync;
              this.isSyncing = false;
              return true;
            }
          } catch (err) {
            // Continue to next version
            continue;
          }
        }
      } catch (error) {
        // Fall through to alternative sync
      } finally {
        this.isSyncing = false;
      }
    }

    // Fallback to localStorage if API sync doesn't work
    await this.tryAlternativeSync(codeToSync);
    return false;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.countryMetafieldSync = new CountryMetafieldSync();
  });
} else {
  window.countryMetafieldSync = new CountryMetafieldSync();
}

// Expose a global function to check country code easily
window.getCountryCode = function() {
  return window.countryCode || 'Not set';
};

